import * as React from "react";
import { Box, TextField, Button, Typography, Grid, Paper, Avatar, IconButton, InputBase, Tooltip, useMediaQuery } from "@mui/material";
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSelector } from "react-redux";
import { useEffect, useRef, useState } from "react";
import config from "config";
import { getAvatarUrl } from '../../../../utils/imageUtils';

const ChatUI = (props) => {
  const { message, sender, onSendMessage, onBack } = props;
  const smallScreen = useMediaQuery("(max-width: 899px)");
  const userId = useSelector((state) => state.main.userId);
  const bottomEl = useRef(null);
  const [messageContent, setMessageContent] = useState("");
  const [sending, setSending] = useState(false);
  const groupedMessages = React.useMemo(() => {
    if (!message?.messages) return [];
    const groups = [];
    const labelFmt = new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
    message.messages.forEach((m) => {
      const d = new Date(Date.parse(m.createdAt));
      const key = d.toDateString();
      const last = groups[groups.length - 1];
      if (!last || last.key !== key) {
        groups.push({ key, label: labelFmt.format(d), items: [] });
      }
      groups[groups.length - 1].items.push(m);
    });
    return groups;
  }, [message?.messages]);

  const handleSend = () => {
    onSendMessage(sender?._id, messageContent, setMessageContent, setSending);
  };

  const handleInputChange = (event) => {
    setMessageContent(event.target.value);
  };

  useEffect(() => {
    const messageContainer = bottomEl.current.parentNode;
    if (
      message?.messages?.length &&
      messageContainer.scrollTop + messageContainer.clientHeight <=
        messageContainer.scrollHeight
    ) {
      messageContainer.scrollTop = messageContainer.scrollHeight;
    }
  }, [message]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (messageContent && !sending) handleSend();
    }
  };

  return (
    <Box className="chatbox w-100">
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          borderRadius: 0,
          px: { xs: 1, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
          background: '#ffffff',
          borderBottom: `1px solid #f3f4f6`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
          flexShrink: 0,
        }}
      >
        {smallScreen && onBack && (
          <IconButton
            onClick={onBack}
            sx={{
              color: '#712d94',
              mr: -0.5,
            }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
        )}
        {getAvatarUrl(sender?.avatar, sender?.gender) && 
        !getAvatarUrl(sender?.avatar, sender?.gender).includes('User-avatar') &&
        !getAvatarUrl(sender?.avatar, sender?.gender).includes('/male.png') &&
        !getAvatarUrl(sender?.avatar, sender?.gender).includes('/female.png') ? (
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              border: 'none',
              borderRadius: 2,
              bgcolor: 'rgba(113,45,148,0.1)',
              boxShadow: 'none',
              '& img': {
                objectFit: 'cover',
              },
            }}
            variant="rounded"
            alt={`${sender?.firstName} ${sender?.lastName}`}
            src={getAvatarUrl(sender?.avatar, sender?.gender)}
            imgProps={{
              onError: (e) => {
                e.target.style.display = 'none';
              }
            }}
          >
            <PersonIcon sx={{ fontSize: 18, color: '#712d94' }} />
          </Avatar>
        ) : (
          <Avatar 
            sx={{ 
              width: 32, 
              height: 32, 
              border: 'none',
              borderRadius: 2,
              bgcolor: 'rgba(113,45,148,0.1)',
              boxShadow: 'none',
            }}
            variant="rounded"
          >
            <PersonIcon sx={{ fontSize: 18, color: '#712d94' }} />
          </Avatar>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <Typography sx={{ fontWeight: 600, lineHeight: 1.2, fontSize: '0.9375rem', color: '#1e293b' }}>
            {sender?.firstName} {sender?.lastName}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          overflowX: "auto",
          p: { xs: 2, sm: 3 },
          background: '#fafafa',
          '&::-webkit-scrollbar': {
            width: '6px',
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#e5e7eb',
            borderRadius: '3px',
            '&:hover': {
              background: '#d1d5db',
            },
          },
        }}
      >
        {groupedMessages.map((group, groupIndex) => (
          <React.Fragment key={group.key || `group-${groupIndex}`}>
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(113,45,148,0.1)',
                  color: '#712d94',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: `1px solid rgba(113,45,148,0.15)`,
                }}
              >
                {group.label}
              </Typography>
            </Box>
            {group.items.map((m, msgIndex) => (
              <Message key={m.id || `msg-${groupIndex}-${msgIndex}`} message={m} userId={userId} />
            ))}
          </React.Fragment>
        ))}
        <div ref={bottomEl}></div>
      </Box>

      {/* Composer */}
      <Box sx={{ p: { xs: 1, md: 1.5 }, backgroundColor: 'background.default', borderRadius: 0, flexShrink: 0 }}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 0.5 },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.75, sm: 1 },
              border: '1px solid #e5e7eb',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundColor: '#ffffff',
            }}
          >
          <Tooltip title="Attach">
            <IconButton size="small" sx={{ color: '#8f8f9a', p: { xs: 0.75, sm: 1 } }}>
              <AttachFileRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <InputBase
            sx={{ 
              flex: 1, 
              py: { xs: 0.75, sm: 1 }, 
              px: { xs: 1, sm: 1 }, 
              fontSize: { xs: 14, sm: 15 } 
            }}
            placeholder="Type a message"
            multiline
            maxRows={4}
            value={messageContent}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />
          <IconButton
            color="primary"
            disabled={!messageContent || sending}
            onClick={handleSend}
            sx={{ mr: 0.5, p: { xs: 0.75, sm: 1 } }}
          >
            <SendRoundedIcon />
          </IconButton>
        </Paper>
      </Box>
    </Box>
  );
};

const Message = ({ message, userId }) => {
  console.log("Message object:", message);
  console.log("Sender object:", message?.sender);
  console.log("Sender ID:", message?.sender?._id);

  const isSender = message?.sender === userId;

  return (
      <Box sx={{ mb: 1, display: 'flex', justifyContent: !isSender ? 'flex-start' : 'flex-end', width: '100%', overflowX: 'auto' }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: !isSender ? "flex-start" : "flex-end",
          maxWidth: { xs: "85%", sm: "75%" },
          minWidth: 0,
          overflowX: 'auto',
        }}
      >
            {!isSender &&
              (message?.sender?.firstName || message?.sender?.lastName) && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6b7280",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    display: 'block',
                    mb: 0.75,
                    px: 0.5,
                  }}
                >
                  {message?.sender?.firstName} {message?.sender?.lastName}
                </Typography>
              )}
            <Box sx={{ display: 'flex', flexDirection: !isSender ? 'row' : 'row-reverse', alignItems: 'flex-end', gap: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.25, sm: 1.5 },
                  px: { xs: 1.5, sm: 1.75 },
                  mb: 0.10,
                  color: isSender ? '#2c2c2c' : '#2c2c2c',
                  background: isSender
                    ? '#F1E5F7'
                    : '#f9fafb',
                  border: isSender ? 'none' : '1px solid #e5e7eb',
                  borderRadius: isSender ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  boxShadow: isSender 
                    ? '0 2px 6px rgba(113,45,148,0.2)' 
                    : '0 1px 2px rgba(0,0,0,0.05)',
                  maxWidth: '100%',
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'visible',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  '&::-webkit-scrollbar': {
                    height: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    borderRadius: '3px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: isSender ? 'rgba(255,255,255,0.3)' : '#e5e7eb',
                    borderRadius: '3px',
                    '&:hover': {
                      background: isSender ? 'rgba(255,255,255,0.5)' : '#d1d5db',
                    },
                  },
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    lineHeight: 1.5,
                    fontSize: '0.9375rem',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflowX: 'auto',
                  }}
                >
                  {message.content}
                </Typography>
              </Paper>
              <Typography
                variant="caption"
                sx={{ 
                  color: '#9ca3af', 
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                  pb: 0.5,
                }}
              >
                {new Date(Date.parse(message.createdAt)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Typography>
            </Box>
      </Box>
    </Box>
  );
};

export default ChatUI;
